/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import PersonIcon from "@material-ui/icons/Person";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import styled from "styled-components";
import CircularProgress from "@material-ui/core/CircularProgress";

import {
  updateFormValue,
  updateAdditonalLegValue,
  buildAdHoc,
} from "../../slices/adHocSlice";
import { fetchServiceLevels } from "../../slices/serviceLevelsSlice";

import { updateSpotFiltersV2 } from "../../slices/spotFiltersSlice";

import { getValidationErrorsFromState } from "../../helpers/adHocHelpers";
import FacilityAutoComplete from "../common/FacilityAutoComplete";
import StyledKeyboardDateTimePicker from "../common/StyledKeyboardDateTimePicker";
import Select from "../common/Select";
import MultiSelect from "../common/MultiSelect";
import types from "../../helpers/types";
import StyledTextField from "../common/StyledTextField";
import Notification from "../common/Notification";

const StyledPersonIcon = styled(({ theme, ...otherProps }) => (
  <PersonIcon {...otherProps} />
))`
  color: ${(props) => props.theme.palette.primary.yellow};
  margin-right: ${(props) => props.theme.spacing(0.5)}px;
  vertical-align: middle;
`;
const CloseButton = styled(({ theme, ...otherProps }) => (
  <IconButton {...otherProps} />
))`
  position: absolute !important;
  right: ${(props) => props.theme.spacing(1)}px;
  top: ${(props) => props.theme.spacing(1)}px;
  color: grey[500];
`;

const BuildAdHocModal = ({ handleClose, open }) => {
  const theme = useTheme();
  const history = useHistory();
  const adHoc = useSelector((state) => state.adHoc);
  const {
    loadOrigin,
    loadOriginSort,
    loadDestination,
    loadDestinationSort,
    movementOrigin,
    movementDestination,
    serviceLevel,
    trailerType,
    driverType,
    requirements,
    numberOfLegs,
    legs,
    formStep,
    requestStatus,
  } = adHoc;
  const isBuilding = requestStatus === types.requestStatuses.inProgress;
  const { serviceLevels, isFetching } = useSelector(
    (state) => state.serviceLevels
  );
  const filters = useSelector((state) => state.spotFilters);

  const dispatch = useDispatch();
  const [showValidation, setShowValidation] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const validation = getValidationErrorsFromState(adHoc);
  const [scheduledDepartureLocal, setScheduledDepartureLocal] = useState(null);
  const [scheduledArrivalLocal, setScheduledArrivalLocal] = useState(null);
  const [scheduledDepartureError, setScheduledDepartureError] = useState(false);
  const [scheduledArrivalError, setScheduledArrivalError] = useState(false);

  useEffect(() => {
    dispatch(fetchServiceLevels());
  }, [dispatch]);

  useEffect(() => {
    if (requestStatus === types.requestStatuses.success) {
      dispatch(
        updateSpotFiltersV2({
          filters,
          history,
        })
      );

      handleCloseModal();
    }
  }, [dispatch, requestStatus]);

  const handleFacilityChange = (key) => (event, option) =>
    dispatch(updateFormValue({ key, value: option }));

  const handleValueChange =
    (key) =>
      ({ target: { value } }) =>
        dispatch(updateFormValue({ key, value }));

  const handleDateTimeChange = (key) => (value) => {
    if (key === "scheduledArrival") {
      setScheduledArrivalLocal(value);
    } else {
      setScheduledDepartureLocal(value);
    }
    if (
      value &&
      typeof value.getMonth === "function" &&
      !isNaN(value.getTime())
    ) {
      dispatch(updateFormValue({ key, value: value.toISOString() }));
    } else {
      dispatch(updateFormValue({ key, value: null }));
    }
  };

  const handleArrivalDepartureDateTimeError = (arrival) => (error) => {
    if (error && typeof error.getMonth === "function") {
      return arrival
        ? setScheduledArrivalError(false)
        : setScheduledDepartureError(false);
    } else {
      return arrival
        ? setScheduledArrivalError(error ? error : false)
        : setScheduledDepartureError(error ? error : false);
    }
  };

  const handleNextFormStep = () => {
    var allTrue = Object.keys(validation).every((k) => validation[k].valid);
    setShowValidation(true);
    if (allTrue) {
      dispatch(updateFormValue({ key: "formStep", value: 2 }));
    }
  };

  const handleBuildAdHoc = async () => {
    await dispatch(buildAdHoc(setSuccessOpen));
  };

  const handleCloseModal = () => {
    setScheduledDepartureLocal(null);
    setScheduledArrivalLocal(null);
    setShowValidation(false);
    handleClose();
  };

  const handleUpdateAdditionalLegValue =
    (key, subKey) =>
      ({ target: { value } }) => {
        let update;
        if (!!legs[key]) {
          update = { ...legs[key], [subKey]: value };
        } else {
          update = { [subKey]: value };
        }
        dispatch(updateAdditonalLegValue({ key, value: update }));
      };

  const getAdditionalInputsByNumberOfLegs = () => {
    let fields = [];
    const isDoubles = requirements.includes(2);
    for (let i = 1; i <= numberOfLegs; i++) {
      fields.push(
        <Grid
          container
          key={`grid-adhoc-${i}`}
          spacing={1}
          data-testid="adhoc-step-2"
        >
          <Grid item xs={1} style={{ alignSelf: "center" }}>
            {i}.
          </Grid>
          <Grid item xs={isDoubles ? 4 : 5}>
            <StyledTextField
              onChange={handleUpdateAdditionalLegValue(i, "routeCode")}
              value={legs[i]?.routeCode ?? ""}
              variant="outlined"
              label="Route Code (optional)"
              disabled={isBuilding}
            />
          </Grid>
          <Grid item xs={isDoubles ? 3 : 5}>
            <StyledTextField
              onChange={handleUpdateAdditionalLegValue(i, "trailer")}
              value={legs[i]?.trailer ?? ""}
              variant="outlined"
              label="Trailer # (optional)"
              disabled={isBuilding}
            />
          </Grid>
          {isDoubles && (
            <Grid item xs={isDoubles ? 4 : 5}>
              <StyledTextField
                onChange={handleUpdateAdditionalLegValue(i, "trailer2")}
                value={legs[i]?.trailer2 ?? ""}
                variant="outlined"
                label="2nd Trailer # (optional)"
                disabled={isBuilding}
              />
            </Grid>
          )}
        </Grid>
      );
    }
    return fields;
  };

  const getParsedSortTypes = () =>
    Object.entries(types.sortTypes).map(([key, value]) => ({
      id: key,
      title: value.description,
    }));

  const getParsedServiceLevels = () =>
    serviceLevels
      .filter((sl) => sl.code !== "E")
      .map((o) => ({ id: o.code, title: o.description }));

  return (
    <>
      <Dialog
        onClose={handleCloseModal}
        open={open}
        maxWidth="lg"
        disableBackdropClick={true}
      >
        <MuiDialogTitle data-testid="adhoc-title">
          <StyledPersonIcon theme={theme} fontSize="small" /> Manual Ad Hoc{" "}
          {formStep === 2 && "Cont."}
          <CloseButton
            theme={theme}
            aria-label="close"
            onClick={handleCloseModal}
          >
            <CloseIcon />
          </CloseButton>
        </MuiDialogTitle>
        <MuiDialogContent>
          {formStep === 1 && (
            <Grid container spacing={1} data-testid="adhoc-step-1">
              <Grid item xs={2}>
                <FacilityAutoComplete
                  handleChange={handleFacilityChange("loadOrigin")}
                  label="Load Origin"
                  selectedFacility={loadOrigin}
                  error={showValidation && !validation.loadOrigin.valid}
                  useAlphaCodeTitle={true}
                />
              </Grid>
              <Grid item xs={2}>
                <Select
                  selectedValue={loadOriginSort}
                  label="Load Origin Sort"
                  inputId="load-origin-sort"
                  options={getParsedSortTypes()}
                  handleChange={handleValueChange("loadOriginSort")}
                  error={showValidation && !validation.loadOriginSort.valid}
                />
              </Grid>
              <Grid item xs={2}>
                <FacilityAutoComplete
                  handleChange={handleFacilityChange("loadDestination")}
                  label="Load Destination"
                  selectedFacility={loadDestination}
                  error={showValidation && !validation.loadDestination.valid}
                  useAlphaCodeTitle={true}
                />
              </Grid>
              <Grid item xs={2}>
                <Select
                  selectedValue={loadDestinationSort}
                  label="Load Dest Sort"
                  inputId="load-dest-sort"
                  options={getParsedSortTypes()}
                  handleChange={handleValueChange("loadDestinationSort")}
                  error={
                    showValidation && !validation.loadDestinationSort.valid
                  }
                />
              </Grid>
              <Grid item xs={2}>
                <FacilityAutoComplete
                  handleChange={handleFacilityChange("movementOrigin")}
                  label="Movement Origin"
                  selectedFacility={movementOrigin}
                  error={showValidation && !validation.movementOrigin.valid}
                  useAlphaCodeTitle={true}
                />
              </Grid>
              <Grid item xs={2}>
                <FacilityAutoComplete
                  handleChange={handleFacilityChange("movementDestination")}
                  label="Movement Destination"
                  selectedFacility={movementDestination}
                  error={
                    showValidation && !validation.movementDestination.valid
                  }
                  useAlphaCodeTitle={true}
                />
              </Grid>
              <Grid item xs={2}>
                <StyledKeyboardDateTimePicker
                  value={scheduledDepartureLocal}
                  onChange={handleDateTimeChange("scheduledDeparture")}
                  onError={handleArrivalDepartureDateTimeError(false)}
                  onAccept={handleDateTimeChange("scheduledDeparture")}
                  error={
                    showValidation &&
                    (!!scheduledDepartureError ||
                      !validation.scheduledDeparture.valid)
                  }
                  helperText={showValidation && scheduledDepartureError}
                  label="Scheduled Depart"
                  ampm={false}
                  variant="inline"
                  minDate={new Date()}
                  minDateMessage="Date should not be before today"
                  format="MM/dd/yyyy HH:mm"
                  inputVariant="outlined"
                />
              </Grid>
              <Grid item xs={2}>
                <StyledKeyboardDateTimePicker
                  value={scheduledArrivalLocal}
                  onChange={handleDateTimeChange("scheduledArrival")}
                  onAccept={handleDateTimeChange("scheduledArrival")}
                  label="Scheduled Arrival"
                  onError={handleArrivalDepartureDateTimeError(true)}
                  error={
                    showValidation &&
                    (!!scheduledArrivalError ||
                      !validation.scheduledArrival.valid)
                  }
                  helperText={showValidation && scheduledArrivalError}
                  ampm={false}
                  minDate={new Date()}
                  variant="inline"
                  minDateMessage="Date should not be before today"
                  format="MM/dd/yyyy HH:mm"
                  inputVariant="outlined"
                />
              </Grid>
              <Grid item xs={2}>
                <Select
                  selectedValue={serviceLevel}
                  label="Service Level"
                  inputId="service-level"
                  handleChange={handleValueChange("serviceLevel")}
                  disabled={isFetching}
                  error={showValidation && !validation.serviceLevel.valid}
                  options={getParsedServiceLevels()}
                />
              </Grid>
              <Grid item xs={2}>
                <Select
                  selectedValue={trailerType}
                  label="Trailer Type"
                  inputId="trailer-type"
                  handleChange={handleValueChange("trailerType")}
                  options={types.spotTrailerTypes}
                  error={showValidation && !validation.trailerType.valid}
                />
              </Grid>
              <Grid item xs={2}>
                <Select
                  selectedValue={driverType}
                  label="Driver Type"
                  inputId="driver-type"
                  handleChange={handleValueChange("driverType")}
                  options={types.spotDriverTypes}
                  error={showValidation && !validation.driverType.valid}
                />
              </Grid>
              <Grid item xs={2}>
                <MultiSelect
                  selectedValues={requirements}
                  label="Requirements"
                  inputId="requirements"
                  handleChange={handleValueChange("requirements")}
                  options={types.spotRequirements}
                />
              </Grid>
            </Grid>
          )}
          {formStep === 2 && <>{getAdditionalInputsByNumberOfLegs()}</>}
        </MuiDialogContent>
        <MuiDialogActions>
          {formStep === 1 && (
            <>
              <StyledTextField
                style={{ width: "100px" }}
                label="# of Legs"
                type="number"
                value={numberOfLegs ? numberOfLegs : ""}
                error={showValidation && !validation.numberOfLegs.valid}
                onChange={handleValueChange("numberOfLegs")}
                variant="outlined"
              />
              <Button
                data-testid="adhoc-next-button"
                variant="contained"
                onClick={handleNextFormStep}
              >
                Next
              </Button>
            </>
          )}
          {formStep === 2 && (
            <Button
              variant="contained"
              onClick={handleBuildAdHoc}
              disabled={isBuilding}
              startIcon={
                isBuilding ? <CircularProgress size={15} /> : undefined
              }
              data-testid="adhoc-build-button"
            >
              Build
            </Button>
          )}
        </MuiDialogActions>
      </Dialog>
      <Notification
        message="AdHoc leg(s) successfully built."
        open={successOpen}
        setOpen={setSuccessOpen}
      />
    </>
  );
};

export default BuildAdHocModal;
